import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../api/client';
import { useChatHistory } from '../hooks/useChatHistory';

interface TreeItem { name: string; path: string; type: 'file' | 'folder'; size?: number; is_indexed?: boolean; }
interface Project { id: string; name: string; description: string; tech_stack: string[]; vector_count: number; file_count: number; user_id?: string; user_name?: string; user_email?: string; isLocal?: boolean; }

export function ProjectsPage() {
  const navigate = useNavigate();
  const { 
    projects, 
    conversations, 
    activeId, 
    loadingProjects,
    createConversation,
    setActiveId,
    deleteConversationsByProject,
    loadProjects,
    activeProjectId,
    setActiveProjectId
  } = useChatHistory();
  const [selected, setSelected] = useState<Project | null>(null);
  const [tree, setTree] = useState<TreeItem[]>([]);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [expandedUsers, setExpandedUsers] = useState<Set<string>>(new Set(['ericmec-drive', 'compute-drive']));
  const [fileContent, setFileContent] = useState<{ path: string; content: string; language: string } | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  const deleteCloudProject = async (projectId: string, projectName: string) => {
    const confirmDelete = confirm(`Bạn có chắc chắn muốn xoá project '${projectName}' khỏi EricMEC Drive? Hành động này cũng sẽ xoá RAG index trên cloud.`);
    if (!confirmDelete) return;

    const deleteChats = confirm(`Bạn có muốn xoá tất cả các đoạn chat liên quan đến project '${projectName}' không?`);
    if (deleteChats) {
      deleteConversationsByProject(projectId);
    }
    try {
      await api.deleteProject(projectId);
      alert(`Đã xoá project '${projectName}' thành công.`);
      loadProjects();
      if (selected?.id === projectId) {
        setSelected(null);
        setTree([]);
        setFileContent(null);
      }
    } catch (e: any) {
      alert(`Lỗi khi xoá project: ${e.message}`);
    }
  };

  const [ericMecToken, setEricMecToken] = useState(() => localStorage.getItem('eric_mec_local_token'));
  const [ericMecWorkspaces, setEricMecWorkspaces] = useState<string[]>(() => {
    const w = localStorage.getItem('eric_mec_workspaces');
    try { return w ? JSON.parse(w) : []; } catch(e) { return []; }
  });

  useEffect(() => {
    const syncToken = () => {
      setEricMecToken(localStorage.getItem('eric_mec_local_token'));
      const w = localStorage.getItem('eric_mec_workspaces');
      try { setEricMecWorkspaces(w ? JSON.parse(w) : []); } catch(e) {}
    };
    window.addEventListener('storage', syncToken);
    return () => window.removeEventListener('storage', syncToken);
  }, []);

  const selectProject = async (p: Project) => {
    setSelected(p);
    setActiveProjectId(p.id);
    setTree([]);
    setFileContent(null);
    try {
      const data = await api.getProjectTree(p.id);
      setTree(data.items ?? []);
    } catch (e: any) {
      console.error('[Projects] Failed to load project tree:', e);
      // Don't alert — just silently show empty tree (local projects may not have cloud tree)
      setTree([]);
    }
  };

  // Auto-select active project from the sidebar
  useEffect(() => {
    if (!selected && !loadingProjects) {
      // 1. Determine target project ID from conversation or context or localStorage
      const activeConv = conversations.find(c => c.id === activeId);
      const targetId = activeConv?.projectId || activeProjectId || localStorage.getItem('ericoder_active_project');

      if (!targetId) {
        // Fallback if no target is defined: prioritize local workspaces if connected, otherwise cloud
        if (ericMecWorkspaces.length > 0) {
          const ws = ericMecWorkspaces[0];
          setSelected({
            id: ws,
            name: ws.split(/[\\/]/).pop() || ws,
            description: `Local workspace directory mapped to EricMEC Compute Drive`,
            tech_stack: [],
            vector_count: 0,
            file_count: 0,
            isLocal: true
          });
          setActiveProjectId(ws);
        } else if (projects.length > 0) {
          selectProject(projects[0]);
        }
        return;
      }

      // 2. Try to match Local Workspace
      const matchedLocal = ericMecWorkspaces.find(ws => ws === targetId || ws.endsWith('\\' + targetId) || ws.endsWith('/' + targetId) || targetId.endsWith('\\' + ws.split(/[\\/]/).pop()));
      if (matchedLocal || targetId.includes('\\') || targetId.includes('/')) {
        const wsPath = matchedLocal || targetId;
        setSelected({
          id: wsPath,
          name: wsPath.split(/[\\/]/).pop() || wsPath,
          description: `Local workspace directory mapped to EricMEC Compute Drive`,
          tech_stack: [],
          vector_count: 0,
          file_count: 0,
          isLocal: true
        });
        setActiveProjectId(wsPath);
        return;
      }

      // 3. Try to match Cloud Project
      const cloudProj = projects.find(p => p.id === targetId);
      if (cloudProj) {
        selectProject(cloudProj);
        return;
      }
    }
  }, [projects, ericMecWorkspaces, conversations, activeId, selected, activeProjectId, loadingProjects]);

  const expandFolder = async (item: TreeItem) => {
    if (item.type !== 'folder') return;
    if (expanded.has(item.path)) {
      setExpanded(e => { const n = new Set(e); n.delete(item.path); return n; });
      return;
    }
    setExpanded(e => new Set([...e, item.path]));
    if (!selected) return;
    const data = await api.getProjectTree(selected.id, item.path);
    const children = (data.items ?? []).map((c: TreeItem) => ({ ...c, _parent: item.path }));
    setTree(t => {
      const idx = t.findIndex(x => x.path === item.path);
      return [...t.slice(0, idx + 1), ...children, ...t.slice(idx + 1).filter(x => !(x as any)._parent?.startsWith(item.path))];
    });
  };

  const openFile = async (item: TreeItem) => {
    if (item.type !== 'file' || !selected) return;
    const data = await api.getFileContent(selected.id, item.path);
    setFileContent(data);
  };

  const toggleUser = (userId: string) => {
    setExpandedUsers(e => {
      const n = new Set(e);
      if (n.has(userId)) n.delete(userId);
      else n.add(userId);
      return n;
    });
  };

  const getFileIcon = (name: string) => {
    if (name.endsWith('.ts') || name.endsWith('.tsx')) return '🟦';
    if (name.endsWith('.js') || name.endsWith('.jsx')) return '🟨';
    if (name.endsWith('.json')) return '{}';
    if (name.endsWith('.md')) return '📝';
    if (name.endsWith('.css')) return '🎨';
    return '📄';
  };

  if (loadingProjects && projects.length === 0) return <div style={{ display: 'flex', justifyContent: 'center', padding: '60px' }}><div className="spinner" /></div>;

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Projects</h1>
          <p className="page-subtitle">RAG-indexed source code on EricMEC</p>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '280px 1fr', gap: '16px', height: 'calc(100vh - 200px)' }}>

          {/* Unified Explorer Treeview */}
          <div className="card" style={{ overflow: 'auto', padding: '12px', display: 'flex', flexDirection: 'column' }}>
            <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginBottom: '8px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
              Explorer
            </div>
            
            <input 
              type="text" 
              placeholder="🔍 Tìm kiếm codebase..." 
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{
                width: '100%',
                padding: '6px 10px',
                borderRadius: '6px',
                border: '1px solid var(--border)',
                background: 'var(--bg-glass)',
                color: 'var(--text-primary)',
                marginBottom: '12px',
                fontSize: '13px'
              }}
            />

            {!ericMecToken ? (
              <div style={{ padding: '20px', textAlign: 'center', color: 'var(--text-muted)', fontSize: '13px' }}>
                <div style={{ fontSize: '24px', marginBottom: '8px' }}>🔗</div>
                Vui lòng kết nối EricMEC để truy cập ổ đĩa ảo.
              </div>
            ) : (
              <div className="file-tree">
                
                {/* Compute Drive (Local Workspace) */}
                {ericMecWorkspaces.length > 0 && (
                  <div key="compute-drive">
                    <div
                      style={{
                        padding: '8px 16px',
                        fontSize: '11px',
                        fontWeight: 700,
                        color: 'var(--text-muted)',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        cursor: 'pointer',
                        userSelect: 'none'
                      }}
                      onClick={() => toggleUser('compute-drive')}
                    >
                      <span style={{ fontSize: '10px', transition: 'transform 0.2s', transform: expandedUsers.has('compute-drive') ? 'rotate(90deg)' : 'rotate(0deg)' }}>▶</span>
                      💻 COMPUTE DRIVE
                    </div>

                    {expandedUsers.has('compute-drive') && ericMecWorkspaces.map(ws => {
                      const isWsSelected = selected?.id === ws && selected.isLocal;
                      const wsName = ws.split(/[\\/]/).pop() || ws;
                      return (
                        <div
                          key={ws}
                          className={`tree-item ${isWsSelected ? 'selected' : ''}`}
                          style={{ paddingLeft: '16px', display: 'flex', justifyContent: 'space-between', paddingRight: '8px' }}
                          onClick={() => {
                            if (isWsSelected) {
                              setSelected(null);
                              setActiveProjectId(null);
                              setTree([]);
                              setFileContent(null);
                            } else {
                              setSelected({
                                id: ws,
                                name: wsName,
                                description: `Local workspace directory mapped to EricMEC Compute Drive`,
                                tech_stack: [],
                                vector_count: 0,
                                file_count: 0,
                                isLocal: true
                              });
                              setActiveProjectId(ws);
                              setTree([]);
                              setFileContent(null);
                            }
                          }}
                        >
                          <div style={{ display: 'flex', alignItems: 'center' }}>
                            <span className="tree-item__icon">📂</span>
                            <span className="tree-item__name" style={{ fontWeight: 500 }}>
                              {wsName}
                              <span style={{ fontSize: '10px', color: 'var(--text-muted)', marginLeft: '6px' }} title={ws}>(Active)</span>
                            </span>
                          </div>
                          <div 
                            style={{ cursor: 'pointer', fontSize: '14px', color: 'var(--text-muted)' }}
                            title="Disconnect Workspace"
                            onClick={(e) => {
                              e.stopPropagation();
                              const deleteChats = confirm(`Ngắt kết nối workspace này? Bạn có muốn xoá tất cả các đoạn chat liên quan đến project này không?`);
                              if (deleteChats) {
                                deleteConversationsByProject(ws);
                              }
                              const newWs = ericMecWorkspaces.filter(w => w !== ws);
                              localStorage.setItem('eric_mec_workspaces', JSON.stringify(newWs));
                              localStorage.setItem('eric_mec_workspaces_dirty', Date.now().toString());
                              setEricMecWorkspaces(newWs);
                              window.dispatchEvent(new Event('storage'));
                              if (selected?.id === ws) {
                                setSelected(null);
                              }
                            }}
                          >
                            ✕
                          </div>
                        </div>
                      );
                    })}

                  </div>
                )}

                {/* EricMEC Drive (Cloud RAG Projects) */}
                <div key="ericmec-drive" style={{ marginTop: ericMecWorkspaces.length > 0 ? '12px' : '0' }}>
                  <div
                    style={{
                      padding: '8px 16px',
                      fontSize: '11px',
                      fontWeight: 700,
                      color: 'var(--text-muted)',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      cursor: 'pointer',
                      userSelect: 'none'
                    }}
                    onClick={() => toggleUser('ericmec-drive')}
                  >
                    <span style={{ fontSize: '10px', transition: 'transform 0.2s', transform: expandedUsers.has('ericmec-drive') ? 'rotate(90deg)' : 'rotate(0deg)' }}>▶</span>
                    ☁️ ERICMEC DRIVE
                  </div>

                  {expandedUsers.has('ericmec-drive') && (() => {
                    const filteredProjects = projects.filter((p: any) => p.name.toLowerCase().includes(searchQuery.toLowerCase()));
                    if (filteredProjects.length === 0) {
                      return (
                        <div style={{ padding: '8px 16px', fontSize: '12px', color: 'var(--text-muted)' }}>
                          Trống (Chưa có Project nào)
                        </div>
                      );
                    }
                    return filteredProjects.map((p: any) => (
                        <div key={p.id}>
                          <div
                            className={`tree-item ${selected?.id === p.id && !fileContent ? 'selected' : ''}`}
                            style={{ paddingLeft: '16px', display: 'flex', justifyContent: 'space-between', paddingRight: '8px' }}
                            onClick={() => {
                              if (selected?.id === p.id) {
                                setSelected(null);
                                setTree([]);
                                setFileContent(null);
                              } else {
                                selectProject(p);
                              }
                            }}
                          >
                            <div>
                              <span className="tree-item__icon" style={{ fontSize: '10px', transition: 'transform 0.2s', transform: selected?.id === p.id ? 'rotate(90deg)' : 'rotate(0deg)' }}>▶</span>
                              <span className="tree-item__name" style={{ fontWeight: 500 }}>📦 {p.name}</span>
                            </div>
                            <div
                              style={{ cursor: 'pointer', fontSize: '14px', color: 'var(--text-muted)', padding: '0 4px' }}
                              title="Delete Project"
                              onClick={(e) => {
                                e.stopPropagation();
                                deleteCloudProject(p.id, p.name);
                              }}
                            >
                              ✕
                            </div>
                          </div>


                          {selected?.id === p.id && tree.map(item => (
                            <div
                              key={item.path}
                              className={`tree-item ${fileContent?.path === item.path ? 'selected' : ''}`}
                              style={{ paddingLeft: `${(((item as any)._parent?.split('/').length ?? 0) * 16) + 36}px` }}
                              onClick={() => item.type === 'folder' ? expandFolder(item) : openFile(item)}
                            >
                              <span className="tree-item__icon">
                                {item.type === 'folder' ? (expanded.has(item.path) ? '📂' : '📁') : getFileIcon(item.name)}
                              </span>
                              <span className="tree-item__name">{item.name}</span>
                              {item.type === 'file' && (
                                <span className="tree-item__badge">
                                  {item.is_indexed
                                    ? <span title="RAG indexed" style={{ color: 'var(--accent-green)', fontSize: '9px' }}>●</span>
                                    : <span title="Not indexed" style={{ color: 'var(--text-muted)', fontSize: '9px' }}>○</span>
                                  }
                                </span>
                              )}
                            </div>
                          ))}
                        </div>
                      ));
                  })()}
                </div>

              </div>
            )}
          </div>

          {/* File Editor Display */}
          <div className="card" style={{ overflow: 'auto', padding: 0 }}>
            {fileContent ? (
              <>
                <div style={{ padding: '10px 16px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <span style={{ fontFamily: 'var(--font-mono)', fontSize: '12px', color: 'var(--text-secondary)' }}>{fileContent.path}</span>
                  <span className="badge badge--gray">{fileContent.language}</span>
                </div>
                <pre style={{
                  margin: 0,
                  padding: '16px',
                  fontFamily: 'var(--font-mono)',
                  fontSize: '12px',
                  lineHeight: '1.7',
                  color: 'var(--text-primary)',
                  overflow: 'auto',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                }}>
                  {fileContent.content}
                </pre>
              </>
            ) : selected ? (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', color: 'var(--text-muted)', padding: '24px' }}>
                <div style={{ fontSize: '64px', marginBottom: '24px' }}>{selected.isLocal ? '💻' : '📦'}</div>
                <div style={{ fontSize: '20px', fontWeight: 700, color: 'var(--text-primary)', marginBottom: '8px' }}>{selected.name}</div>
                <div style={{ fontSize: '13px', color: 'var(--text-secondary)', marginBottom: '24px', textAlign: 'center', maxWidth: '400px' }}>
                  {selected.description}
                </div>
                
                {!selected.isLocal && (
                  <div style={{ display: 'flex', gap: '8px', marginBottom: '32px' }}>
                    <span className="badge badge--purple">{selected.vector_count} vectors</span>
                    <span className="badge badge--blue">{selected.file_count} files</span>
                  </div>
                )}

                <div style={{ display: 'flex', gap: '12px' }}>
                  <button
                    className="btn btn--primary"
                    style={{
                      padding: '10px 24px',
                      borderRadius: '8px',
                      fontSize: '14px',
                      fontWeight: 600,
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      background: 'linear-gradient(135deg, #06b6d4 0%, #3b82f6 100%)',
                      color: '#ffffff',
                      border: 'none',
                      cursor: 'pointer',
                      boxShadow: '0 4px 12px rgba(6,182,212,0.25)',
                    }}
                    onClick={() => {
                      const newConvId = createConversation(selected.id);
                      setActiveId(newConvId);
                      navigate('/chat');
                    }}
                  >
                    💬 Bắt đầu làm việc
                  </button>

                  {!selected.isLocal && (
                    <a
                      href={`${import.meta.env.VITE_API_BASE ?? window.location.origin}/api/projects/${selected.id}/download`}
                      className="btn btn--secondary"
                      style={{
                        padding: '10px 16px',
                        borderRadius: '8px',
                        fontSize: '14px',
                        fontWeight: 600,
                        textDecoration: 'none',
                        color: 'var(--text-primary)',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px'
                      }}
                      download
                    >
                      📥 Tải về (ZIP)
                    </a>
                  )}

                  <button
                    className="btn btn--secondary"
                    style={{
                      padding: '10px 16px',
                      borderRadius: '8px',
                      fontSize: '14px',
                      fontWeight: 600,
                      cursor: 'pointer',
                    }}
                    onClick={() => {
                      if (selected.isLocal) {
                        const deleteChats = confirm(`Ngắt kết nối workspace này? Bạn có muốn xoá tất cả các đoạn chat liên quan đến project này không?`);
                        if (deleteChats) deleteConversationsByProject(selected.id);
                        const newWs = ericMecWorkspaces.filter(w => w !== selected.id);
                        localStorage.setItem('eric_mec_workspaces', JSON.stringify(newWs));
                        localStorage.setItem('eric_mec_workspaces_dirty', Date.now().toString());
                        setEricMecWorkspaces(newWs);
                        window.dispatchEvent(new Event('storage'));
                        setSelected(null);
                      } else {
                        deleteCloudProject(selected.id, selected.name);
                      }
                    }}
                  >
                    ✕ {selected.isLocal ? 'Ngắt kết nối' : 'Xoá Project'}
                  </button>
                </div>
              </div>

            ) : (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: 'var(--text-muted)', fontSize: '13px' }}>
                Select a file from the explorer to view
              </div>
            )}
          </div>
        </div>
    </div>
  );
}

function getFileIcon(name: string): string {
  const ext = name.split('.').pop()?.toLowerCase() ?? '';
  const icons: Record<string, string> = {
    ts: '🔷', tsx: '🔷', js: '🟡', jsx: '🟡', py: '🐍',
    go: '🐹', rs: '🦀', json: '{}', md: '📝', css: '🎨',
    html: '🌐', sql: '🗄', sh: '⚡', yaml: '📋', yml: '📋',
  };
  return icons[ext] ?? '📄';
}
